import {
  isSignInWithEmailLink,
  onAuthStateChanged,
  signInWithEmailLink,
  User,
} from 'firebase/auth';
import { useEffect, useMemo, useState } from 'react';

import Firebase from '@/services/firebase.service';
import { clearApolloSessionCache } from '@/services/graphql/session-cache';

import { FirebaseContext } from '../context/firebase';

export const FirebaseProvider = ({
  children,
  firebase,
}: {
  children: React.ReactNode;
  firebase: Firebase;
}) => {
  const auth = firebase.getAuth();

  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const email: string | null = window.localStorage.getItem('emailForSignIn');

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const handleAuth = async () => {
      const currentUrl = window.location.href;

      if (isSignInWithEmailLink(auth, currentUrl) && email) {
        try {
          await signInWithEmailLink(auth, email, currentUrl);
          window.localStorage.removeItem('emailForSignIn');
          await clearApolloSessionCache();
        } catch (error) {
          console.error('Error signing in with email link', error);
        }
      }

      // After handling the sign-in, set up the auth state listener
      unsubscribe = onAuthStateChanged(auth, (user: User | null) => {
        setAuthenticated(!!user);
        setLoading(false);
      });
    };

    handleAuth();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [auth, email]);

  const contextValue = useMemo(() => ({ authenticated, loading }), [authenticated, loading]);

  return <FirebaseContext.Provider value={contextValue}>{children}</FirebaseContext.Provider>;
};
